import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import { API_BASE_URL } from '../../config/apiEndpoints';
import './NewsletterPopup.css';

// Close icon component
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M12 4L4 12M4 4L12 12" 
      stroke="#333333" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const NewsletterPopup = ({ delayMs = 120000 }) => {
  // Default delay is 2 minutes (120000ms)
  const [showPopup, setShowPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  
  // Ref to track the success close timeout for cleanup
  const successTimeoutRef = useRef(null);

  useEffect(() => {
    // Check if user has already subscribed or dismissed the popup (permanent)
    const hasInteracted = localStorage.getItem('newsletterPopupInteracted');
    
    if (hasInteracted) {
      return; // Don't show popup if user already interacted
    }

    // Check if popup was showing before navigation (e.g., clicked privacy link)
    const wasShowingBeforeNav = sessionStorage.getItem('newsletterPopupShowing');
    if (wasShowingBeforeNav === 'true') {
      setShowPopup(true);
      return;
    }


    // Set timer to show popup after delay
    const timer = setTimeout(() => {
      setShowPopup(true);
      sessionStorage.setItem('newsletterPopupShowing', 'true');
    }, delayMs);

    return () => clearTimeout(timer);
  }, [delayMs]);

  // Cleanup success timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    setShowPopup(false);
    localStorage.setItem('newsletterPopupInteracted', 'dismissed');
    sessionStorage.removeItem('newsletterPopupShowing');
  };

  useCloseOnEscape(showPopup, handleClose);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setSubmitStatus(null);

    const requestBody = {
      email: email.trim(),
      update_enabled: false,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/newsletter/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });


      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        data = null;
      }

      if (response.ok) {
        setSubmitStatus('success');
        localStorage.setItem('newsletterPopupInteracted', 'subscribed');
        sessionStorage.removeItem('newsletterPopupShowing');
        
        // Close popup after success message (with cleanup ref)
        successTimeoutRef.current = setTimeout(() => {
          setShowPopup(false);
        }, 2000);
      } else {
        setSubmitStatus('error');
        if (data?.detail && process.env.NODE_ENV === 'development') {
          console.warn('Newsletter subscribe failed:', data.detail);
        }
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showPopup) {
    return null;
  }

  return (
    <div className="newsletter-popup-overlay" onClick={handleBackdropClick}>
      <div className="newsletter-popup">
        {/* Mobile close button (visible on mobile only) */}
        <button 
          className="newsletter-close-btn-mobile" 
          onClick={handleClose}
          aria-label="Close popup"
        >
          <CloseIcon />
        </button>

        {/* Content Section */}
        <div className="newsletter-content">
          <div className="newsletter-text-section">
            <h2 className="newsletter-title">Early access to your top venues</h2>
            <p className="newsletter-description">
              Join our mailing list for curated event recommendations from our vetted venues, delivered straight to your inbox.
            </p>
          </div>

          <form className="newsletter-form" onSubmit={handleSubmit} noValidate>
            <div className="newsletter-input-wrapper">
              <input
                type="email"
                className="newsletter-input"
                placeholder="Email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                onBlur={(e) => {
                }}
                disabled={isSubmitting || submitStatus === 'success'}
                aria-label="Email address"
              />
            </div>
            
            <button 
              type="submit" 
              className={`newsletter-submit-btn ${submitStatus === 'success' ? 'success' : ''}`}
              disabled={isSubmitting || submitStatus === 'success'}
            >
              {isSubmitting ? 'Subscribing...' : submitStatus === 'success' ? 'Subscribed!' : 'Subscribe'}
            </button>

            {submitStatus === 'error' && (
              <p className="newsletter-error">Subscription failed. Please try again.</p>
            )}
          </form>

          <p className="newsletter-privacy">
            Unsubscribe at any time. Read our{' '}
            <Link to="/privacy" className="newsletter-privacy-link">
              privacy policy
            </Link>{' '}
            for more information
          </p>
        </div>

        {/* Image Section */}
        <div className="newsletter-image-section">
          <img 
            src="/newsletter-venue.jpg" 
            alt="Beautiful venue with green plants and marble tables"
            className="newsletter-image"
            width={1000}
            height={682}
            loading="lazy"
            decoding="async"
          />
          
          {/* Desktop close button (on top of image) */}
          <button 
            className="newsletter-close-btn-desktop" 
            onClick={handleClose}
            aria-label="Close popup"
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
